import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import { getFilenameFromContentDisposition } from "../../../helpers";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import {
  IBulkInvoices,
  ICSIOInvoicingDetail,
  ICSIOInvoicingInformation,
  IInvoiceDetails,
  IInvoiceInformation,
  IInvoicePaymentHistory,
  IInvoiceTransactions,
  IJeEntryInvoiceTransactions,
} from "./InvoiceInformation.types";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import {
  IDatatableV2AdvancedSearchFilter,
} from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { uploadFile } from "../../../features/FileUpload/utils";

export const FETCH_ACCOUNTING_INVOICE_INFORMATION_PROGRESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_PROGRESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_SUCCESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_SUCCESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_FAILURE =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_FAILURE";

export const fetchAccountingInvoiceInformationProgress = () =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_PROGRESS);

export const fetchAccountingInvoiceInformationSuccess = (
  data: IInvoiceInformation
) => action(FETCH_ACCOUNTING_INVOICE_INFORMATION_SUCCESS, { data });

export const fetchAccountingInvoiceInformationFailed = (errorMessage: string) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_FAILURE, { errorMessage });

export const fetchAccountingInvoiceInformationAsync =
  (invoiceId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchAccountingInvoiceInformationProgress());

        const res = await api.get(
          `/accounting/get-account-invoice?invoice_id=${invoiceId}`
        );
        const data: IInvoiceInformation[] = res.data.data;
        if (data.length > 0) {
          dispatch(fetchAccountingInvoiceInformationSuccess(data[0]));
        }
      } catch (err: any) {
        dispatch(
          fetchAccountingInvoiceInformationFailed("Something went to be wrong!")
        );
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const addAccountingInvoiceInformationAysnc =
  (
    data: IInvoiceInformation,
    onCallback: (isSuccess: boolean, data?: IInvoiceInformation) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.post("/accounting/edit-account-invoice", {
          ...data,
        });
        const message = "Invoice Information saved successfully.";
        dispatch(
          showMessage({
            type: "success",
            message: message,
            displayAs: "snackbar",
          })
        );
        onCallback(true, res.data.data);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };
export const addAccountingBulkInvoicesAysnc =
  (
    data: IBulkInvoices[],
    file: File | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const asPayload = {
          customer_id: data[0].code,
          policy_type: data[0].policy_type,
          customer_policy_id: data[0].policy_id,
          page_name: "Auto Attachment",
        };

        const path = await uploadFile(
          file,
          "POLICY",
          data[0].attachment || "",
          asPayload
        );
        await api.post("/accounting/insert-bulk-invoices-and-bills", {
          INVOICES: data.map((item) => ({
            ...item,
            attachment: path,
          })),
        });
        const message = "Invoices Information saved successfully.";
        dispatch(
          showMessage({
            type: "success",
            message: message,
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const FETCH_INVOICE_PAYMENT_HISTORY_PROGRESS =
  "FETCH_INVOICE_PAYMENT_HISTORY_PROGRESS";
export const FETCH_INVOICE_PAYMENT_HISTORY_SUCCESS =
  "FETCH_INVOICE_PAYMENT_HISTORY_SUCCESS";
export const FETCH_INVOICE_PAYMENT_HISTORY_FAILED =
  "FETCH_INVOICE_PAYMENT_HISTORY_FAILED";

export const fetchInvoicePaymentHistoryProgress = () =>
  action(FETCH_INVOICE_PAYMENT_HISTORY_PROGRESS);
export const fetchInvoicePaymentHistorySuccess = (
  data: IInvoicePaymentHistory
) =>
  action(FETCH_INVOICE_PAYMENT_HISTORY_SUCCESS, {
    data,
  });
export const fetchInvoicePaymentHistoryFailed = (errorMessage: string) =>
  action(FETCH_INVOICE_PAYMENT_HISTORY_FAILED, { errorMessage });

export const fetchInvoicePaymentHistoryAsync =
  (invoiceId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchInvoicePaymentHistoryProgress());

        const res = await api.get(
          `/accounting/get-account-invoice-and-payment-history?invoice_id=${invoiceId}`
        );
        const data: IInvoicePaymentHistory[] = res.data.data;
        if (data.length > 0) {
          dispatch(fetchInvoicePaymentHistorySuccess(data[0]));
        } else {
          dispatch(
            fetchInvoicePaymentHistoryFailed(ERROR_MESSAGES.NO_RECORD_FOUND)
          );
        }
      } catch (err: any) {
        dispatch(fetchInvoicePaymentHistoryFailed(ERROR_MESSAGES.SERVER_ERROR));
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_PROGRESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_PROGRESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_SUCCESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_SUCCESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_FAILURE =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_FAILURE";

export const fetchAccountingInvoiceInformationListProgress = () =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_PROGRESS);

export const fetchAccountingInvoiceInformationListSuccess = (
  data: IInvoiceDetails[],
  totalRecords: number
) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_SUCCESS, {
    data,
    totalRecords,
  });

export const fetchAccountingInvoiceInformationListFailed = (
  errorMessage: string
) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_FAILURE, { errorMessage });

export const fetchAccountingInvoiceInformationListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    policyId?: any,
    customerId?: any
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchAccountingInvoiceInformationListProgress());
        let finalUrl = `/accounting/get-account-invoice?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
        if (searchValue && searchValue.length > 0) {
          finalUrl = `/accounting/get-account-invoice?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&advanceFilter=${JSON.stringify(
            searchValue
          )}&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }
        if (status !== "-1") {
          finalUrl += "&status=" + status;
        }

        // if(customerId){
        //   finalUrl = finalUrl+"&customer_id="+customerId;
        // }

        if (policyId) {
          finalUrl = finalUrl + "&policy_id=" + policyId;
        }

        const res = await api.get(finalUrl);
        const data: IInvoiceDetails[] = res.data.data;
        dispatch(
          fetchAccountingInvoiceInformationListSuccess(
            data,
            res.data.totalRecords
          )
        );
      } catch (err: any) {
        dispatch(
          fetchAccountingInvoiceInformationListFailed(
            "Something went to be wrong!"
          )
        );
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };
export const downloadInvoiceInformationPDFAsync =
  (
    invoice_number: number | string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.get(
          `/accounting/get-account-invoice-pdf?accn_invoice_transaction_items_id=${invoice_number}`,
          {
            responseType: "arraybuffer",
          }
        );

        const pdfBuffer = res.data;

        const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

        const pdfUrl = URL.createObjectURL(pdfBlob);

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = getFilenameFromContentDisposition(res);
        link.click();

        URL.revokeObjectURL(pdfUrl);
        onCallback();
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const previewInvoiceInformationPDFAsync =
  (
    invoice_number: number | string,
    onCallback: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.get(
          `/accounting/get-account-invoice-pdf?accn_invoice_transaction_items_id=${invoice_number}&is_invoice_details_preview=true`
        );

        for (const key in res.data.data) {
          const newTab = window.open("", "_blank");
          if (newTab) {
            newTab.document.write(res.data.data[key]);
            newTab.document.close();
          }
        }
        onCallback();
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const downloadInvoiceTransactionItemsPDFAsync =
  (
    accn_invoice_transaction_items_id: number[],
    onCallback: (data: string) => void,
    customerStatementData?: {
      customer_policy_id?: number;
      customer_id?: number[];
      from_date?: string;
      to_date?: string;
      is_customer_statement?: boolean;
    }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.post(`/accounting/preview-account-invoice-pdf`, {
          accn_invoice_transaction_items_id,
          is_invoice_details_preview: false,
          is_customer_statement: customerStatementData?.is_customer_statement || false,
          customer_policy_id: customerStatementData?.customer_policy_id,
          customer_id: customerStatementData?.customer_id,
          from_date: customerStatementData?.from_date,
          to_date: customerStatementData?.to_date,
        });
        const data = res.data.data;
        onCallback(data);
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const previewInvoiceTransactionItemsPDFAsync =
  (
    accn_invoice_transaction_items_id: number[],
    onCallback: () => void,
    customerStatementData?: {
      customer_policy_id?: number[];
      customer_id?: number[];
      from_date?: string;
      to_date?: string;
      is_customer_statement?: boolean;
    }
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.post(`/accounting/preview-account-invoice-pdf`, {
          accn_invoice_transaction_items_id,
          is_invoice_details_preview: true,
          customer_statement_data:
            customerStatementData?.is_customer_statement || false,
          customer_policy_id: customerStatementData?.customer_policy_id,
          customer_id: customerStatementData?.customer_id,
          from_date: customerStatementData?.from_date,
          to_date: customerStatementData?.to_date,
        });

        for (const key in res.data.data) {
          const newTab = window.open("", "_blank");
          if (newTab) {
            newTab.document.write(res.data.data[key]);
            newTab.document.close();
          }
        }
        onCallback();
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

/**-------------------------- */

export const FETCH_ACCOUNTING_CSIO_INVOICING_LIST_PROGRESS =
  "FETCH_ACCOUNTING_CSIO_INVOICING_LIST_PROGRESS";
export const FETCH_ACCOUNTING_CSIO_INVOICING_LIST_SUCCESS =
  "FETCH_ACCOUNTING_CSIO_INVOICING_LIST_SUCCESS";
export const FETCH_ACCOUNTING_CSIO_INVOICING_LIST_FAILURE =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_FAILURE";

export const fetchAccountingCSIOInvoicingListProgress = () =>
  action(FETCH_ACCOUNTING_CSIO_INVOICING_LIST_PROGRESS);

export const fetchAccountingCSIOInvoicingListSuccess = (
  data: ICSIOInvoicingInformation[]
) =>
  action(FETCH_ACCOUNTING_CSIO_INVOICING_LIST_SUCCESS, {
    data,
  });

export const fetchAccountingCSIOInvoicingListFailed = (errorMessage: string) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_LIST_FAILURE, { errorMessage });

export const fetchAccountingCSIOInvoicingListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    policyId?: any,
    customerId?: any
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchAccountingCSIOInvoicingListProgress());
        let finalUrl = `/csio/get-csio-temp-invoices?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
        if (searchValue && searchValue.length > 0) {
          finalUrl = `/csio/get-csio-temp-invoices?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&advanceFilter=${JSON.stringify(
            searchValue
          )}&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }
        if (status !== "-1") {
          finalUrl += "&status=" + status;
        }

        // if(customerId){
        //   finalUrl = finalUrl+"&customer_id="+customerId;
        // }

        if (policyId) {
          finalUrl = finalUrl + "&policy_id=" + policyId;
        }

        const res = await api.get(finalUrl);
        const data: ICSIOInvoicingInformation[] = res.data.data;
        dispatch(fetchAccountingCSIOInvoicingListSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchAccountingCSIOInvoicingListFailed("Something went to be wrong!")
        );
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const upsertCSIOInvoicingMappingAsync =
  (
    data: {
      message_guid: string;
      temp_invoice_code: string;
    }[],
    run_all_failed_inovices: number,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        await api.post("/csio/re-run-failed-invoices", {
          data: data,
          run_all_failed_inovices: run_all_failed_inovices,
        });

        onCallback(true);
        //   dispatch(updateIsMap(isMap, messageGUID));
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertCSIOCommissionAsync =
  (
    data: {
      message_guid: string;
      temp_invoice_code: string | null;
      commission_percentage: string;
      commission_amount: string;
    },
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        await api.put("/csio/update-csio-temp-invoice-commission", data);
        dispatch(
          showMessage({
            type: "success",
            message: "Commission updated successfully.",
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

/*-----------------------------------------------*/
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_PROGRESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_PROGRESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_SUCCESS =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_SUCCESS";
export const FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_FAILURE =
  "FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_FAILURE";

export const fetchAccountingInvoiceInformationTransactionsListProgress = () =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_PROGRESS);

export const fetchAccountingInvoiceInformationTransactionsListSuccess = (
  data: IInvoiceTransactions[],
  totalRecords: number
) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_SUCCESS, {
    data,
    totalRecords,
  });

export const fetchAccountingInvoiceInformationTransactionsListFailed = (
  errorMessage: string
) =>
  action(FETCH_ACCOUNTING_INVOICE_INFORMATION_TRANSACTIONS_LIST_FAILURE, {
    errorMessage,
  });

export const fetchInvoiceTransactionsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    policyId?: any,
    customerId?: any,
    transaction_type?: string,
    invoice_for?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(fetchAccountingInvoiceInformationTransactionsListProgress());
        const invoiceUrl = "/accounting/get-account-invoice-transaction-items";
        const draftInvoiceUrl = "/accounting/get-account-invoice-transaction-items-draft";
        const isDraftOrServiceFee = status === "DRAFT" || status === "SERVICE FEE";
        let finalUrl = `${isDraftOrServiceFee ? draftInvoiceUrl : invoiceUrl}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;

        const advanceFilters: any[] = [];
        if (searchValue && searchValue.length > 0) {
          advanceFilters.push(...searchValue);
        }


        if (advanceFilters.length > 0) {
          finalUrl = `${isDraftOrServiceFee ? draftInvoiceUrl : invoiceUrl}?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}`;

        }

        if (invoice_for && invoice_for !== "-1") {
          advanceFilters.push({
            column: ["invoice_for"],
            operator: "EQUAL",
            value: invoice_for,
            logicalOperator: "AND",
          });
        }

        if (advanceFilters.length > 0) {
          finalUrl += `&advanceFilter=${encodeURIComponent(
            JSON.stringify(advanceFilters)
          )}`;
        }


        if (status !== "-1") {
          finalUrl += "&status=" + status;
        }

        if (policyId) {
          finalUrl = finalUrl + "&policy_id=" + policyId;
        }
        if (transaction_type) {
          finalUrl = finalUrl + "&transaction_type=" + transaction_type;
        }


        const res = await api.get(finalUrl);
        const data: IInvoiceTransactions[] = res.data.data;
        dispatch(
          fetchAccountingInvoiceInformationTransactionsListSuccess(
            data,
            res.data.totalRecords
          )
        );
      } catch (err: any) {
        dispatch(
          fetchAccountingInvoiceInformationTransactionsListFailed(
            "Something went to be wrong!"
          )
        );
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      }
    };

export const fetchInvoiceTransactionItemsByIdAsync =
  (
    invoicePaymentId: number,
    onCallback: (data: IInvoiceTransactions) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        let finalUrl = `/accounting/get-account-invoice-transaction-items`;
        if (invoicePaymentId) {
          const filterObj = {
            column: ["accn_invoice_transaction_items_id"],
            operator: "EQUAL",
            value: invoicePaymentId,
            logicalOperator: "AND",
          };
          finalUrl += `?advanceFilter=${JSON.stringify(filterObj)}`;
        }

        const res = await api.get(finalUrl);

        const data: IInvoiceTransactions[] = res.data.data;
        if (data.length > 0) {
          onCallback(data[0]);
        } else {
          onCallback({} as IInvoiceTransactions);
          dispatch(
            showMessage({
              type: "error",
              message: "No transaction found",
              displayAs: "snackbar",
            })
          );
        }
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertInvoiceTransactionItems =
  (
    payload: IInvoiceTransactions,
    file: File | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      const { create_ts, insert_ts, duplicate_indicator, ...rest } = payload;
      try {
        dispatch(saveLoaderProgress());
        const asPayload = {
          customer_id: payload.code,
          policy_type: payload.policy_type,
          customer_policy_id: payload.policy_id,
          page_name: "Auto Attachment",
        };
        const path = await uploadFile(
          file,
          "POLICY",
          payload.attachment ?? "",
          asPayload
        );
        await api.put(
          "/accounting/update-account-invoice-transaction-items",
          {
            ...rest,
            attachment: path,
          }
        );
        dispatch(
          showMessage({
            type: "success",
            message: "Invoice transaction Saved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };
export const upsertInvoiceMultipleTransactionItems =
  (
    payload: IInvoiceTransactions[],
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        await api.put(
          "/accounting/update-account-invoice-transaction-items",
          payload
        );
        dispatch(
          showMessage({
            type: "success",
            message: "Invoice transactions Approved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const updateMultipleStatusOfInvoiceTransactions =
  (
    data: IInvoiceTransactions[],
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        await api.post("/accounting/insert-bulk-invoices-and-bills", {
          INVOICES: data,
        });
        const message = "Invoices Information saved successfully.";
        dispatch(
          showMessage({
            type: "success",
            message: message,
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };



export const upsertCSIOInvoiceTransactionItems =
  (
    payload: ICSIOInvoicingDetail,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        await api.put("/csio/update-csio-temp-invoice-details", payload);
        dispatch(
          showMessage({
            type: "success",
            message: "CSIO invoice transaction Saved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };
export const upsertJEEntryInvoiceTransactionItems =
  (
    payload: IJeEntryInvoiceTransactions,
    onCallback: (isSuccess: boolean, data: IJeEntryInvoiceTransactions) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      const { create_ts, insert_ts, duplicate_indicator, ...rest } = payload;
      try {
        dispatch(saveLoaderProgress());
        const res = await api.post(
          "/accounting/edit-account-je-entry-invoice-transaction-items",
          rest
        );
        const data: IJeEntryInvoiceTransactions = res.data.data;
        dispatch(
          showMessage({
            type: "success",
            message: "Invoice transaction Saved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true, data);
      } catch (err: any) {
        onCallback(false, {} as IJeEntryInvoiceTransactions);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

/*--------------------------------------------------*/

export const updateInvoiceTransactionItemsReconciliationDataAsync =
  (
    payload: IInvoiceTransactions,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        dispatch(saveLoaderProgress());
        const res = await api.put(
          "/accounting/update-account-transaction-items-reconciliation-record",
          {
            accn_invoice_transaction_items_id:
              payload.accn_invoice_transaction_items_id,
            reconciled: payload.reconciled,
          }
        );
        dispatch(
          showMessage({
            type: "success",
            message: "Data Saved successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback(true);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const CLEAR_ACCOUNTING_INVOICE_INFORMATION =
  "CLEAR_ACCOUNTING_INVOICE_INFORMATION";
export const clearAccountingInvoiceInformation = () =>
  action(CLEAR_ACCOUNTING_INVOICE_INFORMATION);
